import { useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Card, Badge, DataTable, SlidePanel, PanelSection, SpecGrid, SpecCell,
  Modal, FormGroup, FormTextarea, FormSelect, toast, type Column,
} from '@/components';
import { useApi } from '@/api/hooks';
import { api } from '@/api/client';
import { useRole } from '@/hooks/useRole';
import styles from './Leads.module.css';

interface Plan {
  id: number;
  name: string;
  price_monthly: number;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  company_name: string | null;
  phone: string | null;
  tel: string | null;
  plan_interest: number | null;
  plan_name: string | null;
  status: 'new' | 'contacted' | 'approved' | 'converted' | 'rejected';
  notes: string | null;
  slug: string | null;
  approval_token: string | null;
  approval_token_expires_at: string | null;
  converted_tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

const statusLabel: Record<string, string> = {
  new: '新規', contacted: '連絡済', approved: '承認済',
  converted: '転換済', rejected: '却下',
};
const statusVariant: Record<string, 'blue' | 'amber' | 'green' | 'teal' | 'red'> = {
  new: 'blue', contacted: 'amber', approved: 'green',
  converted: 'teal', rejected: 'red',
};

export function Leads() {
  const { isAdmin } = useRole();
  const navigate = useNavigate();
  const { data: leads, loading, refetch } = useApi<Lead[]>('/console/leads');
  const { data: plans } = useApi<Plan[]>('/console/plans');

  const [selected, setSelected] = useState<Lead | null>(null);
  const [statusFilter, setStatusFilter] = useState('全て');
  const [searchText, setSearchText] = useState('');
  const [notes, setNotes] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: '', email: '', company_name: '', phone: '', plan_interest: '', notes: '' });
  const [sending, setSending] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [localSetupUrl, setLocalSetupUrl] = useState<string | null>(null);
  const [showApproveConfirm, setShowApproveConfirm] = useState(false);
  const [approvePlanId, setApprovePlanId] = useState<string>('');

  // ── フィルター ─────────────────────────────
  const filtered = !leads ? [] : leads.filter((l) => {
    if (statusFilter !== '全て' && statusLabel[l.status] !== statusFilter) return false;
    const q = searchText.toLowerCase();
    if (q &&
      !l.name.toLowerCase().includes(q) &&
      !l.email.toLowerCase().includes(q) &&
      !(l.company_name ?? '').toLowerCase().includes(q)) return false;
    return true;
  });

  const newCount = leads?.filter((l) => l.status === 'new').length ?? 0;

  // ── 詳細パネルを開く ───────────────────────
  const openDetail = (lead: Lead) => {
    setSelected(lead);
    setNotes(lead.notes ?? '');
    setShowRejectModal(false);
    setLocalSetupUrl(null);
  };

  // ── 新規作成 ───────────────────────────────
  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.email.trim()) {
      toast('名前とメールは必須です', 'error');
      return;
    }
    setSending(true);
    try {
      await api('/console/leads', {
        method: 'POST',
        body: JSON.stringify({
          name: createForm.name.trim(),
          email: createForm.email.trim(),
          company_name: createForm.company_name.trim() || null,
          phone: createForm.phone.trim() || null,
          plan_interest: createForm.plan_interest ? Number(createForm.plan_interest) : null,
          notes: createForm.notes.trim() || null,
        }),
      });
      toast('リードを作成しました');
      setShowCreate(false);
      setCreateForm({ name: '', email: '', company_name: '', phone: '', plan_interest: '', notes: '' });
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '作成に失敗しました', 'error');
    } finally {
      setSending(false);
    }
  };

  // ── メモ保存（blur時） ─────────────────────
  const handleSaveNotes = async () => {
    if (!selected || notes === (selected.notes ?? '')) return;
    try {
      await api(`/console/leads/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes }),
      });
      toast('メモを保存しました');
      refetch();
    } catch {
      toast('保存に失敗しました', 'error');
    }
  };

  // ── プラン変更 ─────────────────────────────
  const handlePlanChange = async (planId: number | null) => {
    if (!selected) return;
    try {
      await api(`/console/leads/${selected.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ plan_interest: planId }),
      });
      const plan = planId ? (plans ?? []).find(p => p.id === planId) : null;
      setSelected(prev => prev ? { ...prev, plan_interest: planId, plan_name: plan?.name ?? null } : prev);
      toast('プランを更新しました');
      refetch();
    } catch {
      toast('プランの更新に失敗しました', 'error');
    }
  };

  // ── 承認 ───────────────────────────────────
  const handleApproveClick = () => {
    if (!selected) return;
    if (!selected.slug && !selected.plan_interest) {
      setApprovePlanId('');
      setShowApproveConfirm(true);
      return;
    }
    handleApprove();
  };

  const handleApproveWithPlan = async () => {
    if (!selected) return;
    // モーダルでプランが選択された場合、先に保存
    if (approvePlanId) {
      await handlePlanChange(Number(approvePlanId));
    }
    handleApprove();
  };

  const handleApprove = async () => {
    if (!selected) return;
    setShowApproveConfirm(false);
    setSending(true);
    try {
      const res = await api<{ success: boolean; setupUrl?: string; tenantUrl?: string }>(
        `/console/leads/${selected.id}/approve`,
        { method: 'POST' },
      );
      if (res.tenantUrl) {
        toast('テナントを作成し、案内メールを送信しました');
        setLocalSetupUrl(res.tenantUrl);
        setSelected((prev) => prev ? { ...prev, status: 'converted' } : prev);
      } else {
        toast('承認メールを送信しました');
        setLocalSetupUrl(res.setupUrl ?? '');
        setSelected((prev) => prev ? { ...prev, status: 'approved' } : prev);
      }
      refetch();
    } catch (e) {
      toast(e instanceof Error ? e.message : '承認に失敗しました', 'error');
    } finally {
      setSending(false);
    }
  };

  // ── 却下（理由入力モーダル） ────────────────
  const handleReject = async () => {
    if (!selected) return;
    setSending(true);
    try {
      await api(`/console/leads/${selected.id}/reject`, {
        method: 'POST',
        body: JSON.stringify({ reason: rejectReason.trim() || undefined }),
      });
      toast('却下しました（通知メール送信済み）');
      setShowRejectModal(false);
      setRejectReason('');
      refetch();
      setSelected(null);
    } catch {
      toast('却下に失敗しました', 'error');
    } finally {
      setSending(false);
    }
  };


  // ── セットアップURL取得 ────────────────────
  const getSetupUrl = (lead: Lead): string | null => {
    if (localSetupUrl) return localSetupUrl;
    if (lead.approval_token) {
      const base = import.meta.env.VITE_SETUP_BASE_URL || 'https://jiayin.jp';
      return `${base}/setup?token=${lead.approval_token}`;
    }
    return null;
  };

  // ── DataTable 列定義 ───────────────────────
  const columns: Column<Lead>[] = [
    {
      key: 'name',
      label: '名前 / メール',
      width: '200px',
      render: (r) => (
        <div>
          <div className={styles.tdMain}>{r.name}</div>
          <div className={styles.tdSub}>{r.email}</div>
        </div>
      ),
    },
    {
      key: 'company_name',
      label: '会社名',
      render: (r) => <span>{r.company_name ?? '—'}</span>,
    },
    {
      key: 'plan_name',
      label: 'プラン',
      render: (r) =>
        r.plan_name ? (
          <Badge variant="blue">{r.plan_name}</Badge>
        ) : (
          <span style={{ color: 'var(--ink3)' }}>—</span>
        ),
    },
    {
      key: 'status',
      label: 'ステータス',
      render: (r) => (
        <span className={`${styles.pill} ${styles[`pill_${statusVariant[r.status] ?? 'gray'}`]}`}>
          {statusLabel[r.status] ?? r.status}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: '申込日',
      render: (r) => (
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink2)' }}>
          {new Date(r.created_at).toLocaleDateString('ja-JP')}
        </span>
      ),
    },
    {
      key: 'id',
      label: '',
      render: (r) => (
        <button
          className={styles.detailBtn}
          onClick={(e) => { e.stopPropagation(); openDetail(r); }}
        >
          詳細
        </button>
      ),
    },
  ];

  // ── JSX ───────────────────────────────────
  return (
    <div className={styles.page}>
      {/* Toolbar */}
      <div className={styles.toolbar}>
        <FormSelect
          className={styles.filterSelect}
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option>全て</option>
          <option>新規</option>
          <option>連絡済</option>
          <option>承認済</option>
          <option>転換済</option>
          <option>却下</option>
        </FormSelect>
        <input
          className={styles.searchInput}
          type="text"
          placeholder="名前・メール・会社名で検索"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
        {isAdmin && (
          <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>
            + 新規リード
          </button>
        )}
      </div>

      {/* 一覧 */}
      <Card title={`リード一覧（${filtered.length}件）${newCount > 0 ? ` — 新規 ${newCount}件` : ''}`}>
        {loading ? (
          <div style={{ padding: 20, color: 'var(--ink3)', fontSize: 13 }}>読込中...</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 20, color: 'var(--ink3)', fontSize: 13 }}>
            {leads?.length === 0 ? '申請はまだありません。' : '条件に一致するリードがありません。'}
          </div>
        ) : (
          <DataTable columns={columns} rows={filtered} keyField="id" onRowClick={openDetail} />
        )}
      </Card>

      {/* 詳細 SlidePanel */}
      <SlidePanel
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.name ?? 'リード詳細'}
        actions={
          selected ? (
            <>
              {isAdmin && selected.status !== 'approved' &&
                selected.status !== 'converted' &&
                selected.status !== 'rejected' && (
                  <button
                    className={styles.btnPrimary}
                    onClick={handleApproveClick}
                    disabled={sending}
                  >
                    承認
                  </button>
                )}
              {isAdmin && selected.status !== 'rejected' && selected.status !== 'converted' && (
                <button
                  className={styles.btnSecondary}
                  onClick={() => { setRejectReason(''); setShowRejectModal(true); }}
                  disabled={sending}
                >
                  却下
                </button>
              )}
            </>
          ) : undefined
        }
      >
        {selected && (
          <>
            {/* ヘッダー情報 */}
            <PanelSection>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
                <div className={styles.leadAv}>
                  {selected.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>{selected.name}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--ink2)', marginTop: 3 }}>
                    {selected.email}
                  </div>
                </div>
                <span
                  className={`${styles.pill} ${styles[`pill_${statusVariant[selected.status] ?? 'gray'}`]}`}
                  style={{ marginLeft: 'auto' }}
                >
                  {statusLabel[selected.status] ?? selected.status}
                </span>
              </div>
              <SpecGrid>
                <SpecCell label="会社名" value={selected.company_name ?? '—'} />
                <SpecCell label="電話番号" value={selected.phone || selected.tel || '—'} mono />
                <SpecCell label="希望プラン">
                  {(() => {
                    const editable = selected.status !== 'converted' && selected.status !== 'rejected';
                    const isManualLead = !selected.slug && (!selected.plan_interest || (selected.notes ?? '').startsWith('問い合わせから変換'));
                    if (isAdmin && editable && isManualLead) {
                      return (
                        <FormSelect
                          value={selected.plan_interest ?? ''}
                          onChange={e => handlePlanChange(e.target.value ? Number(e.target.value) : null)}
                          style={{ fontSize: 12, padding: '2px 6px', minWidth: 140 }}
                        >
                          <option value="">未設定</option>
                          {(plans ?? []).filter(p => p.id !== 1).map(p => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </FormSelect>
                      );
                    }
                    return <span>{selected.plan_name ?? '—'}</span>;
                  })()}
                </SpecCell>
                {selected.slug && <SpecCell label="テナントID" value={selected.slug} mono />}
                <SpecCell
                  label="申込日"
                  value={new Date(selected.created_at).toLocaleDateString('ja-JP')}
                  mono
                />
                <SpecCell
                  label="更新日"
                  value={new Date(selected.updated_at).toLocaleDateString('ja-JP')}
                  mono
                />
              </SpecGrid>
            </PanelSection>

            {/* メモ */}
            <PanelSection title="メモ（社内用）">
              <FormTextarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={handleSaveNotes}
                placeholder="社内メモを入力（フォーカスを外すと自動保存）"
                style={{ width: '100%', minHeight: 100, resize: 'vertical' }}
              />
            </PanelSection>

            {/* セットアップURL（承認済・転換済） */}
            {(selected.status === 'approved' || selected.status === 'converted') && getSetupUrl(selected) && (
              <PanelSection title="セットアップURL">
                <div className={styles.setupUrlRow}>
                  <code className={styles.setupUrlCode}>{getSetupUrl(selected)}</code>
                  <button
                    className={styles.btnSm}
                    onClick={() => {
                      const url = getSetupUrl(selected);
                      if (url) {
                        navigator.clipboard.writeText(url);
                        toast('URLをコピーしました');
                      }
                    }}
                  >
                    コピー
                  </button>
                </div>
                {selected.approval_token_expires_at && (
                  <div style={{ fontSize: 11, color: 'var(--ink3)', marginTop: 6 }}>
                    有効期限: {new Date(selected.approval_token_expires_at).toLocaleString('ja-JP')}
                  </div>
                )}
              </PanelSection>
            )}

            {/* テナントURL（トライアル承認後） */}
            {selected.status === 'converted' && selected.slug && (
              <PanelSection title="テナントURL">
                <div className={styles.setupUrlRow}>
                  <code className={styles.setupUrlCode}>{localSetupUrl || `https://${selected.slug}.jiayin.jp/tenant/login`}</code>
                  <button
                    className={styles.btnSm}
                    onClick={() => {
                      const url = localSetupUrl || `https://${selected.slug}.jiayin.jp/tenant/login`;
                      navigator.clipboard.writeText(url);
                      toast('URLをコピーしました');
                    }}
                  >
                    コピー
                  </button>
                </div>
              </PanelSection>
            )}

            {/* 転換済テナントリンク */}
            {selected.status === 'converted' && selected.converted_tenant_id && (
              <PanelSection title="転換済テナント">
                <button
                  className={styles.btnSecondary}
                  onClick={() => navigate('/tenants')}
                >
                  テナント一覧で確認 →
                </button>
              </PanelSection>
            )}
          </>
        )}
      </SlidePanel>

      {/* プラン未設定の承認確認モーダル */}
      <Modal
        open={showApproveConfirm}
        onClose={() => setShowApproveConfirm(false)}
        title="プラン未設定の確認"
        width={440}
        footer={
          <>
            <button className={styles.btnGhost} onClick={() => setShowApproveConfirm(false)}>
              キャンセル
            </button>
            <button className={styles.btnPrimary} onClick={handleApproveWithPlan} disabled={sending}>
              {sending ? '処理中...' : approvePlanId ? '承認する' : 'プラン未設定のまま承認する'}
            </button>
          </>
        }
      >
        <div className={styles.infoBanner} style={{ background: 'var(--amber-dim, #fff8e1)', border: '1px solid var(--amber, #ffc107)', marginBottom: 12 }}>
          希望プランが未設定です。プランを選択してから承認できます。
        </div>
        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>プランを選択</label>
          <FormSelect
            value={approvePlanId}
            onChange={e => setApprovePlanId(e.target.value)}
            style={{ width: '100%' }}
          >
            <option value="">未設定（スタンダードが適用されます）</option>
            {(plans ?? []).filter(p => p.id !== 1).map(p => (
              <option key={p.id} value={p.id}>{p.name}（¥{p.price_monthly.toLocaleString()}/月）</option>
            ))}
          </FormSelect>
        </div>
      </Modal>

      {/* 新規リード作成モーダル */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="新規リード作成"
        width={480}
        footer={
          <>
            <button className={styles.btnGhost} onClick={() => setShowCreate(false)}>
              キャンセル
            </button>
            <button className={styles.btnPrimary} onClick={handleCreate} disabled={sending}>
              {sending ? '作成中...' : '作成'}
            </button>
          </>
        }
      >
        <FormGroup label="名前 *">
          <input
            className={styles.searchInput}
            style={{ width: '100%' }}
            type="text"
            value={createForm.name}
            onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))}
            placeholder="山田 太郎"
          />
        </FormGroup>
        <FormGroup label="メール *">
          <input
            className={styles.searchInput}
            style={{ width: '100%' }}
            type="email"
            value={createForm.email}
            onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))}
            placeholder="taro@example.com"
          />
        </FormGroup>
        <FormGroup label="会社名">
          <input
            className={styles.searchInput}
            style={{ width: '100%' }}
            type="text"
            value={createForm.company_name}
            onChange={e => setCreateForm(f => ({ ...f, company_name: e.target.value }))}
            placeholder="株式会社○○不動産"
          />
        </FormGroup>
        <FormGroup label="電話番号">
          <input
            className={styles.searchInput}
            style={{ width: '100%' }}
            type="tel"
            value={createForm.phone}
            onChange={e => setCreateForm(f => ({ ...f, phone: e.target.value }))}
            placeholder="03-1234-5678"
          />
        </FormGroup>
        <FormGroup label="希望プラン">
          <FormSelect
            value={createForm.plan_interest}
            onChange={e => setCreateForm(f => ({ ...f, plan_interest: e.target.value }))}
            style={{ width: '100%' }}
          >
            <option value="">未設定</option>
            {(plans ?? []).filter(p => p.id !== 1).map(p => (
              <option key={p.id} value={p.id}>{p.name}（¥{p.price_monthly.toLocaleString()}/月）</option>
            ))}
          </FormSelect>
        </FormGroup>
        <FormGroup label="メモ">
          <FormTextarea
            value={createForm.notes}
            onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
            placeholder="流入経路・備考など"
            style={{ width: '100%', minHeight: 80 }}
          />
        </FormGroup>
      </Modal>

      {/* 却下理由モーダル */}
      <Modal
        open={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        title="リード却下"
        width={440}
        footer={
          <>
            <button className={styles.btnGhost} onClick={() => setShowRejectModal(false)}>キャンセル</button>
            <button className={styles.btnDanger ?? styles.btnSecondary} onClick={handleReject} disabled={sending}>
              {sending ? '処理中...' : '却下する'}
            </button>
          </>
        }
      >
        <p style={{ fontSize: 13, marginBottom: 8 }}>
          <strong>{selected?.name}</strong>（{selected?.email}）を却下します。
        </p>
        <p style={{ fontSize: 12, color: 'var(--ink3)', marginBottom: 12 }}>
          却下通知メールがリード本人に送信されます。
        </p>
        <FormGroup label="却下理由（任意）">
          <FormTextarea
            value={rejectReason}
            onChange={e => setRejectReason(e.target.value)}
            placeholder="例: 対象エリア外、競合他社利用中 など"
            style={{ width: '100%', minHeight: 80 }}
          />
        </FormGroup>
      </Modal>
    </div>
  );
}
